import { Globe, Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { APP_LANGUAGES, resolveLanguage } from '@/services/i18n/languages'
import { useTheme } from '@/theme'

/**
 * Footer controls for the unauthenticated auth screens (login / register):
 * a theme toggle and a language switcher. The language change is applied to
 * the UI only (`i18n.changeLanguage`) — there's no signed-in user yet to
 * persist the preference to; that happens later via Settings.
 *
 * The switcher cycles through `APP_LANGUAGES` and is labelled with the
 * language it switches TO, so one button covers however many we ship.
 */
export function AuthFooterControls() {
  const { t, i18n } = useTranslation()
  const { theme, setTheme } = useTheme()

  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

  const current = resolveLanguage(i18n.language)
  const currentIndex = APP_LANGUAGES.findIndex(l => l.code === current)
  const next = APP_LANGUAGES[(currentIndex + 1) % APP_LANGUAGES.length]

  return (
    <div className="mt-8 flex items-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setTheme(isDark ? 'light' : 'dark')}
        className="h-9 text-muted-foreground md:h-8"
        aria-label={isDark ? t('nav.lightMode') : t('nav.darkMode')}
      >
        {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        {isDark ? t('nav.lightMode') : t('nav.darkMode')}
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => i18n.changeLanguage(next.code)}
        className="h-9 text-muted-foreground md:h-8"
        aria-label={next.label}
      >
        <Globe className="h-4 w-4" />
        {next.label}
      </Button>
    </div>
  )
}
